import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Search, Package, MapPin, Calendar, Weight, Clock, CheckCircle,
  Truck, Plane, FileText, Download, ChevronDown, ChevronUp,
  AlertCircle, DollarSign, ArrowRight
} from 'lucide-react';

// Mock tracking data
const mockShipments = {
  'BN-45821-JM': {
    tracking: 'BN-45821-JM',
    manifestNo: 'MAN-2025-12-15-001',
    bagNo: 'BAG-FS-2025-089',
    status: 'in_transit',
    statusText: 'In Transit to Jamaica',
    origin: 'Miami Warehouse, FL',
    destination: 'Kingston Hub, JA',
    weight: '12.5 lbs',
    dimensions: '18" x 12" x 8"',
    estimatedArrival: 'Dec 18, 2025',
    shippedDate: 'Dec 14, 2025',
    timeline: [
      { status: 'Package Received in Florida', location: 'Miami, FL', date: 'Dec 12, 2025', time: '10:30 AM', completed: true },
      { status: 'Packed in Dedicated Bag', location: 'Miami Warehouse', date: 'Dec 13, 2025', time: '2:15 PM', completed: true, details: 'Bag: BAG-FS-2025-089' },
      { status: 'Manifest Created', location: 'Miami Warehouse', date: 'Dec 14, 2025', time: '8:00 AM', completed: true, details: 'Manifest: MAN-2025-12-15-001' },
      { status: 'Departed Miami', location: 'Miami International Airport', date: 'Dec 15, 2025', time: '11:45 AM', completed: true, current: true },
      { status: 'In Transit', location: 'Caribbean Air Cargo', date: 'Expected: Dec 16', completed: false },
      { status: 'Arrived in Jamaica', location: 'Norman Manley International', date: 'Expected: Dec 17', completed: false },
      { status: 'Customs Clearance', location: 'Kingston Customs', date: 'Expected: Dec 17', completed: false },
      { status: 'Ready for Pickup', location: 'Kingston Hub', date: 'Expected: Dec 18', completed: false },
    ],
    charges: {
      freight: 3250.50,
      service: 1000,
      pallet: 1296,
      delivery: 1000,
      total: 6546.50
    }
  },
  'BN-45789-JM': {
    tracking: 'BN-45789-JM',
    manifestNo: 'MAN-2025-12-10-002',
    bagNo: 'BAG-FS-2025-082',
    status: 'ready',
    statusText: 'Ready for Pickup',
    origin: 'Miami Warehouse, FL',
    destination: 'Kingston Hub, JA',
    weight: '8.2 lbs',
    dimensions: '14" x 10" x 6"',
    arrivedDate: 'Dec 15, 2025',
    timeline: [
      { status: 'Package Received in Florida', location: 'Miami, FL', date: 'Dec 8, 2025', time: '9:15 AM', completed: true },
      { status: 'Packed in Dedicated Bag', location: 'Miami Warehouse', date: 'Dec 9, 2025', time: '3:30 PM', completed: true },
      { status: 'Manifest Created', location: 'Miami Warehouse', date: 'Dec 10, 2025', time: '7:45 AM', completed: true },
      { status: 'Departed Miami', location: 'Miami International Airport', date: 'Dec 11, 2025', time: '10:20 AM', completed: true },
      { status: 'Arrived in Jamaica', location: 'Norman Manley International', date: 'Dec 14, 2025', time: '2:15 PM', completed: true },
      { status: 'Customs Cleared', location: 'Kingston Customs', date: 'Dec 15, 2025', time: '9:30 AM', completed: true },
      { status: 'Ready for Pickup', location: 'Kingston Hub', date: 'Dec 15, 2025', time: '11:00 AM', completed: true, current: true },
    ],
    charges: {
      freight: 2134.20,
      service: 1000,
      pallet: 1296,
      delivery: 1000,
      total: 5430.20,
      paid: true
    }
  }
};

export default function CustomerTracking() {
  const [searchParams] = useSearchParams();
  const initialTracking = searchParams.get('id') || '';
  
  const [trackingNumber, setTrackingNumber] = useState(initialTracking);
  const [searchedTracking, setSearchedTracking] = useState(initialTracking);
  const [shipment, setShipment] = useState(initialTracking ? mockShipments[initialTracking] : null);
  const [isSearching, setIsSearching] = useState(false);
  const [showCharges, setShowCharges] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = () => {
    setIsSearching(true);
    setError('');
    
    setTimeout(() => {
      const foundShipment = mockShipments[trackingNumber.toUpperCase()];
      if (foundShipment) {
        setShipment(foundShipment);
        setSearchedTracking(trackingNumber.toUpperCase());
      } else {
        setShipment(null);
        setError('No shipment found with that tracking number. Try: BN-45821-JM or BN-45789-JM');
      }
      setIsSearching(false);
    }, 800);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'in_transit': return <Truck size={24} />;
      case 'ready': return <CheckCircle size={24} />;
      case 'awaiting_payment': return <AlertCircle size={24} />;
      default: return <Package size={24} />;
    }
  };

  return (
    <div className="tracking-page" data-testid="tracking-page">
      {/* Search Section */}
      <div className="tracking-search-card">
        <div className="search-header">
          <Package size={32} className="search-icon" />
          <h2>Track Your Package</h2>
          <p>Enter your tracking number to see real-time status updates</p>
        </div>

        <div className="tracking-search-box">
          <div className="search-input-wrapper">
            <Search size={20} />
            <input
              type="text"
              placeholder="Enter tracking number (e.g., BN-45821-JM)"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              data-testid="tracking-input"
            />
          </div>
          <button 
            className="btn btn-primary"
            onClick={handleSearch}
            disabled={isSearching || !trackingNumber}
            data-testid="search-tracking-btn"
          >
            {isSearching ? 'Searching...' : 'Track'}
          </button>
        </div>

        {error && (
          <div className="tracking-error" data-testid="tracking-error">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        <div className="demo-tracking-hint">
          <p>Try these demo tracking numbers: <code>BN-45821-JM</code> or <code>BN-45789-JM</code></p>
        </div>
      </div>

      {/* Shipment Results */}
      {shipment && (
        <div className="tracking-results" data-testid="tracking-results">
          {/* Status Hero */}
          <div className={`tracking-status-hero ${shipment.status}`}>
            <div className="status-hero-icon">
              {getStatusIcon(shipment.status)}
            </div>
            <div className="status-hero-content">
              <h2>{shipment.statusText}</h2>
              <p className="tracking-number-display">Tracking: {shipment.tracking}</p>
            </div>
            {shipment.status === 'ready' && (
              <div className="pickup-badge">
                <CheckCircle size={16} />
                Available for Pickup
              </div>
            )}
          </div>

          {/* Quick Info Cards */}
          <div className="tracking-info-grid">
            <div className="tracking-info-card">
              <MapPin size={20} />
              <div>
                <span className="info-label">Origin</span>
                <span className="info-value">{shipment.origin}</span>
              </div>
            </div>
            <div className="tracking-info-card">
              <MapPin size={20} />
              <div>
                <span className="info-label">Destination</span>
                <span className="info-value">{shipment.destination}</span>
              </div>
            </div>
            <div className="tracking-info-card">
              <Weight size={20} />
              <div>
                <span className="info-label">Weight</span>
                <span className="info-value">{shipment.weight}</span>
              </div>
            </div>
            <div className="tracking-info-card">
              <Calendar size={20} />
              <div>
                <span className="info-label">{shipment.estimatedArrival ? 'Est. Arrival' : 'Arrived'}</span>
                <span className="info-value">{shipment.estimatedArrival || shipment.arrivedDate}</span>
              </div>
            </div>
          </div>

          {/* Shipment Details */}
          <div className="shipment-details-card">
            <h3>Shipment Details</h3>
            <div className="details-grid">
              <div className="detail-row">
                <span className="detail-label">Manifest Number</span>
                <span className="detail-value">{shipment.manifestNo}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Bag Number</span>
                <span className="detail-value">{shipment.bagNo}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Dimensions</span>
                <span className="detail-value">{shipment.dimensions}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Ship Date</span>
                <span className="detail-value">{shipment.shippedDate || shipment.timeline[0].date}</span>
              </div>
            </div>
          </div>

          {/* Full Timeline */}
          <div className="tracking-timeline-card">
            <h3>Tracking Timeline</h3>
            <div className="tracking-timeline">
              {shipment.timeline.map((event, index) => (
                <div 
                  key={index}
                  className={`timeline-event ${event.completed ? 'completed' : ''} ${event.current ? 'current' : ''}`}
                  data-testid={`timeline-event-${index}`}
                >
                  <div className="timeline-marker">
                    {event.completed ? (
                      <CheckCircle size={20} />
                    ) : (
                      <div className="marker-empty" />
                    )}
                  </div>
                  <div className="timeline-connector" />
                  <div className="timeline-event-content">
                    <div className="event-header">
                      <span className="event-status">{event.status}</span>
                      {event.current && <span className="current-badge">Current</span>}
                    </div>
                    <div className="event-details">
                      <span className="event-location">
                        <MapPin size={12} /> {event.location}
                      </span>
                      <span className="event-datetime">
                        <Clock size={12} /> {event.date} {event.time && `at ${event.time}`}
                      </span>
                    </div>
                    {event.details && (
                      <span className="event-extra">{event.details}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Charges Breakdown (Transparency Engine) */}
          <div className="charges-card">
            <button 
              className="charges-header"
              onClick={() => setShowCharges(!showCharges)}
              data-testid="toggle-charges"
            >
              <div className="charges-title">
                <DollarSign size={20} />
                <h3>Charges & Fees Breakdown</h3>
              </div>
              <div className="charges-toggle">
                <span className="charges-total">JMD {shipment.charges.total.toLocaleString()}</span>
                {showCharges ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </button>

            {showCharges && (
              <div className="charges-breakdown" data-testid="charges-breakdown">
                <div className="charge-row">
                  <div className="charge-label">
                    <Plane size={16} />
                    <span>Freight Charge</span>
                    <span className="charge-formula">({shipment.weight} ÷ 2.2) × $2.78 × 165</span>
                  </div>
                  <span className="charge-amount">JMD {shipment.charges.freight.toLocaleString()}</span>
                </div>
                <div className="charge-row">
                  <div className="charge-label">
                    <Package size={16} />
                    <span>Service Contribution</span>
                    <span className="charge-formula">Per bag rate</span>
                  </div>
                  <span className="charge-amount">JMD {shipment.charges.service.toLocaleString()}</span>
                </div>
                <div className="charge-row">
                  <div className="charge-label">
                    <FileText size={16} />
                    <span>Pallet Fee</span>
                    <span className="charge-formula">Storage & handling</span>
                  </div>
                  <span className="charge-amount">JMD {shipment.charges.pallet.toLocaleString()}</span>
                </div>
                <div className="charge-row">
                  <div className="charge-label">
                    <Truck size={16} />
                    <span>Delivery Fee</span>
                    <span className="charge-formula">Flat rate</span>
                  </div>
                  <span className="charge-amount">JMD {shipment.charges.delivery.toLocaleString()}</span>
                </div>
                <div className="charge-row total">
                  <span>Total</span>
                  <span className="charge-amount">JMD {shipment.charges.total.toLocaleString()}</span>
                </div>
                
                {shipment.charges.paid ? (
                  <div className="payment-status paid">
                    <CheckCircle size={16} />
                    <span>Paid</span>
                  </div>
                ) : (
                  <button className="btn btn-primary full-width" data-testid="pay-invoice-btn">
                    Pay Now <ArrowRight size={16} />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* No Results State */}
      {!shipment && searchedTracking && !error && (
        <div className="no-results">
          <Package size={48} />
          <h3>No Results</h3>
          <p>Enter a tracking number above to see shipment details</p>
        </div>
      )}
    </div>
  );
}
